import { tryOnUnmounted } from '@vueuse/core';
import type { Ref } from 'vue';
import { computed, nextTick, watch } from 'vue';
import { useState } from '@/composable/useState';
import type { Serializer } from '@/composable/useStorage';
import { getDefaultSerializer } from '@/composable/useStorage';
import { hookBefore } from '@/core/lib/utils';

// @ts-expect-error we patch the method to mark it as patched
if (!sessionStorage.setItem.__patched__) {
    hookBefore(sessionStorage, 'setItem', (key: string, newValue: string) => {
        const oldValue = sessionStorage.getItem(key);
        const event = new StorageEvent('storage', {
            key,
            newValue,
            oldValue,
            storageArea: sessionStorage,
        });
        nextTick(() => window.dispatchEvent(event));
    });

    hookBefore(sessionStorage, 'removeItem', (key: string) => {
        const oldValue = sessionStorage.getItem(key);
        const event = new StorageEvent('storage', {
            key,
            newValue: undefined,
            oldValue,
            storageArea: sessionStorage,
        });
        nextTick(() => window.dispatchEvent(event));
    });

    // @ts-expect-error we patch the method to mark it as patched
    sessionStorage.setItem.__patched__ = true;
}

function tryParseFn<T>(decode: (input: string) => T) {
    return (str: string | null, defaultValue: T): T => {
        try {
            if (str === '' || str === null || str === undefined) {
                return defaultValue;
            }

            return decode(str);
        } catch (err) {
            console.error('useSessionStorage parse failed with value:', str, err);
            return defaultValue;
        }
    };
}

export function useSessionStorage<T>(key: string, defaultValue: T, options?: Serializer<T>): [Ref<T>, (newVal: T) => void] {
    const defaultSerializer = getDefaultSerializer(defaultValue);
    const decode = options?.decode ?? defaultSerializer.decode;
    const encode = options?.encode ?? defaultSerializer.encode;

    const initValue = sessionStorage.getItem(key) ?? '';
    const [rawValue, setRawValue] = useState<string>(initValue);
    watch(rawValue, value => sessionStorage.setItem(key, value));

    const tryParse = tryParseFn(decode);
    const target = computed<T>(() => tryParse(rawValue.value, defaultValue));

    const setValue = (newValue: T) => {
        const encoded = encode(newValue);
        sessionStorage.setItem(key, encoded);
        setRawValue(encoded);
    };
    const value = computed({
        get: () => target.value,
        set: setValue,
    });

    const eventCallback = (event: StorageEvent): void => {
        if (event.storageArea === sessionStorage && event.key === key) {
            setRawValue(event.newValue ?? '');
        }
    };

    window.addEventListener('storage', eventCallback);
    tryOnUnmounted(() => {
        window.removeEventListener('storage', eventCallback);
    });

    return [value, setValue];
}
